/**
 * Модель для работы с отчётом "Рабочее время"
 */

import { createModel } from '@rematch/core';
import { RootModel } from './index';
import { saveAs } from 'file-saver';

import { IEmployeeShort, IReportWorkingTime } from './types';
import setStartEndOfDay from '../utils/setStartEndOfDay';

import UsersService from '../services/UsersService';
import ReportService from '../services/ReportService';
import AdminService from '../services/AdminService';
import moment from 'moment';

interface IData {
  employees: IEmployeeShort[];
  timeTable: any[];
}

interface IList {
  data: IReportWorkingTime[] | null;
  pagesCount: number;
}

interface IState {
  data: IData;
  list: IList;
}

const initialState: IState = {
  data: {
    employees: [],
    timeTable: []
  },
  list: {
    data: null,
    pagesCount: 0
  }
};

export const reportWorkingTime = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setData(state: IState, payload: IData): IState {
      return { ...state, data: payload };
    },
    setList(state: IState, payload: IList): IState {
      return { ...state, list: payload };
    },
    resetList(state: IState): IState {
      return {
        ...state,
        list: {
          data: null,
          pagesCount: 0
        }
      };
    },
    reset(): IState {
      return { ...initialState };
    }
  },
  effects: () => ({
    async getData() {
      try {
        const [{ data: employees }, { data: timeTable }] = await Promise.all([
          UsersService.getAllEmployeesShort(),
          AdminService.getTimeTableConfig()
        ]);

        this.setData({ employees, timeTable });
      } catch (error) {
        return error;
      }
    },
    async getList(params: any, rootState?: any) {
      const { timeTableId, filterType } = params;
      const isTableMode = filterType === 'Table';
      const timeTableListObject = timeTableId
        ? rootState.reportWorkingTime.data.timeTable.find((option: any) => option.id === timeTableId)
        : {};

      const cloneParams = setStartEndOfDay(params, ['from', 'to']);
      if (isTableMode && cloneParams.from) {
        cloneParams.to = moment(cloneParams.from).add(1, 'day').format();
      }

      try {
        const { data } = await ReportService.getWorkingTime(
          timeTableId
            ? {
                ...params,
                ...{
                  from: moment(
                    `${moment(params.from).format('L')} ${
                      timeTableListObject && timeTableListObject.startTime
                        ? timeTableListObject.startTime
                        : moment(params.from)
                            .startOf('day')
                            .format('LT')
                    }`,
                    'L LT'
                  ).format(),
                  to: moment(
                    `${moment(params.from).format('L')} ${
                      timeTableListObject && timeTableListObject.startTime
                        ? timeTableListObject.startTime
                        : moment(params.from)
                            .endOf('day')
                            .format('LT')
                    }`,
                    'L LT'
                  )
                    .add(
                      Number(
                        timeTableListObject && timeTableListObject.timetableDuration
                          ? timeTableListObject.timetableDuration
                          : 0
                      ),
                      'hours'
                    )
                    .format()
                }
              }
            : cloneParams
        );

        this.setList(data);
      } catch (error) {
        return error;
      }
    },
    async exportList(params: any, rootState?: any) {
      const { timeTableId, filterType } = params;
      const isTableMode = filterType === 'Table';
      const timeTableListObject = timeTableId
        ? rootState.reportWorkingTime.data.timeTable.find((option: any) => option.id === timeTableId)
        : {};
      const cloneParams = setStartEndOfDay(params, ['from', 'to']);
      if (isTableMode && cloneParams.from) {
        cloneParams.to = moment(cloneParams.from).add(1, 'day').format();
      }
      const editParams = {
        ...params,
        ...{
          from: moment(
            `${moment(params.from).format('L')} ${
              timeTableListObject && timeTableListObject.startTime
                ? timeTableListObject.startTime
                : moment(params.from)
                    .startOf('day')
                    .format('LT')
            }`,
            'L LT'
          ).format(),
          to: moment(
            `${moment(params.from).format('L')} ${
              timeTableListObject && timeTableListObject.startTime
                ? timeTableListObject.startTime
                : moment(params.from)
                    .endOf('day')
                    .format('LT')
            }`,
            'L LT'
          )
            .add(
              Number(
                timeTableListObject && timeTableListObject.timetableDuration
                  ? timeTableListObject.timetableDuration
                  : 0
              ),
              'hours'
            )
            .format()
        }
      };
      const { from, to } = timeTableId ? editParams : cloneParams;

      try {
        const { data } = await ReportService.getXlsxWorkingTime(
          timeTableId ? editParams : cloneParams
        );

        saveAs(data, `Отчет по рабочему времени за период ${from} - ${to}.xlsx`);
      } catch (error) {
        return error;
      }
    }
  })
});
